import { c as _c } from "react/compiler-runtime";
import { HPIInfoModal } from "@/components/optimization/hpi-info-modal";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/radix/tooltip";
import { useHousePricingStore } from "@/stores/seller/house-pricing-store";
import React, { useCallback, useState } from "react";
import currencify from "@/utils/currency/format";
import { formatPricePerSqft } from "@/utils/currency/get-rate";
import { formatRange } from "@/utils/format-range";
import { type ComparablePrice } from "../optimization/types";

// ---------------------------------------
// 1. FORMAT DATA HOOK
// ---------------------------------------

interface UseFormattedPropertyData {
  prices: number[];
  currentRate?: number;
  builtUpAreas: number[];
  parkings: number[];
  comparablePrices?: ComparablePrice[];
  plotAreas: number[];
  distance?: number;
}
const useFormattedPropertyData = ({
  prices,
  currentRate,
  builtUpAreas,
  parkings,
  comparablePrices,
  plotAreas,
  distance
}: UseFormattedPropertyData) => {
  // Format distance - show in m or km with space
  const distanceStr = distance ? distance >= 1000 ? `${(distance / 1000).toFixed(1)} km` : `${Math.round(distance)} m` : "0 m";
  const priceNum = prices?.[0];
  const priceStr = priceNum && priceNum > 0 ? currencify({
    amount: priceNum,
    abbreviated: true
  }) : "";
  const rateStr = (currentRate ? Math.round(currentRate) : formatPricePerSqft({
    price: priceNum || 0,
    area: builtUpAreas?.[0] || 0
  }) || "").toLocaleString();
  const estimatedPriceToday = comparablePrices?.[0]?.estimatedPriceToday || 0;
  const estimatedPriceTodayStr = estimatedPriceToday > 0 ? currencify({
    amount: estimatedPriceToday,
    abbreviated: true
  }) : "";
  const estimatedPriceTodayPerSqft = estimatedPriceToday > 0 ? formatPricePerSqft({
    price: estimatedPriceToday,
    area: builtUpAreas?.[0] || 0
  }) as string : "";
  const parkingsStr = formatRange({
    data: parkings
  });
  const builtUpAreasStr = formatRange({
    data: builtUpAreas,
    formatter: v => v.toLocaleString()
  });
  const plotAreaStr = (plotAreas?.[0] || "").toLocaleString();
  return {
    priceStr,
    rateStr,
    estimatedPriceTodayStr,
    estimatedPriceTodayPerSqft,
    parkingsStr,
    builtUpAreasStr,
    plotAreaStr,
    distanceStr
  };
};

// ---------------------------------------
// 2. DYNAMIC LAYOUT BUILDER
// ---------------------------------------

interface LayoutBlock {
  type: "primary" | "secondary" | "meta";
  value: string;
  prefixLabel?: string;
  suffixLabel?: string;
  underline?: boolean;
  tooltip?: string;
  button?: boolean;
  onClick?: () => void;
}
interface LayoutResult {
  left: LayoutBlock[];
  right: LayoutBlock[];
}
interface BuildLayout {
  supType?: string;
  forWhat: string;
  priceStr: string;
  rateStr: string;
  estimatedPriceTodayStr: string;
  estimatedPriceTodayPerSqft: string;
  parkingsStr: string;
  builtUpAreasStr: string;
  parkings: number[];
  propertyTypes: string[];
  constructionRate?: number;
  plotRate?: number;
  distanceStr: string;
}
interface BuildLayoutParams extends BuildLayout {
  onEstimatedPriceClick?: () => void;
}
const buildLayout = ({
  supType,
  forWhat,
  priceStr,
  rateStr,
  estimatedPriceTodayStr,
  estimatedPriceTodayPerSqft,
  parkingsStr,
  builtUpAreasStr,
  parkings,
  propertyTypes,
  constructionRate,
  plotRate,
  distanceStr,
  onEstimatedPriceClick
}: BuildLayoutParams): LayoutResult => {
  const isHouse = propertyTypes?.some(pt => pt.toLowerCase() === "house");
  const priceSuffix = supType === "sp" ? "+" : "";
  const layout: LayoutResult = {
    left: [],
    right: []
  };

  // SOLD & CANCELLED
  if (forWhat === "sold_and_cancelled") {
    if (isHouse) {
      // LEFT SIDE (META INFO)

      // Display Plot Rate from store if available
      if (plotRate) {
        layout.left.push({
          type: "primary" as const,
          value: `₹${plotRate.toLocaleString()}`,
          prefixLabel: "Plot Rate"
        });
      }

      // Display Construction Rate from store if available
      if (constructionRate) {
        layout.left.push({
          type: "secondary" as const,
          value: `₹${constructionRate.toLocaleString()}`,
          prefixLabel: "Construction Rate"
        });
      }
      if (estimatedPriceTodayPerSqft) {
        layout.right.push({
          type: "secondary" as const,
          value: estimatedPriceTodayPerSqft,
          suffixLabel: "/sqft",
          tooltip: "Estimated rate per sqft"
        });
      }
      if (estimatedPriceTodayStr) {
        layout.right.push({
          type: "secondary" as const,
          value: `₹${estimatedPriceTodayStr} ${priceSuffix} (estd.)`,
          underline: true,
          tooltip: `Estimated price today: ${estimatedPriceTodayStr} (as per HPI)`,
          onClick: onEstimatedPriceClick
        });
      }
    }

    // DEFAULT
    else {
      // LEFT SIDE (META INFO)
      if (estimatedPriceTodayPerSqft) {
        layout.left.push({
          type: "primary" as const,
          value: estimatedPriceTodayPerSqft,
          suffixLabel: "sft"
        });
      }
      if (estimatedPriceTodayStr) {
        layout.left.push({
          type: "secondary" as const,
          value: `₹${estimatedPriceTodayStr} ${priceSuffix} (estd.)`,
          underline: true,
          tooltip: `Estimated price today: ${estimatedPriceTodayStr} (as per HPI)`,
          onClick: onEstimatedPriceClick
        });
      }

      // RIGHT SIDE (META INFO)
      if (distanceStr) {
        layout.right.push({
          type: "meta" as const,
          value: distanceStr,
          tooltip: "Distance from your property"
        });
      }
      if (rateStr) {
        layout.right.push({
          type: "secondary" as const,
          value: rateStr,
          suffixLabel: "sft"
        });
      }
    }
  }

  // LIVE
  else if (forWhat === "live") {
    if (isHouse) {
      // LEFT SIDE (META INFO)

      // Display Plot Rate from store if available
      if (plotRate) {
        layout.left.push({
          type: "primary" as const,
          value: `₹${plotRate.toLocaleString()}`,
          prefixLabel: "Plot Rate"
        });
      }

      // Display Construction Rate from store if available
      if (constructionRate) {
        layout.left.push({
          type: "secondary" as const,
          value: `₹${constructionRate.toLocaleString()}`,
          prefixLabel: "Construction Rate"
        });
      }

      // RIGHT SIDE (META INFO)
      if (rateStr) {
        layout.right.push({
          type: "primary" as const,
          value: rateStr,
          tooltip: "Rate per sqft",
          suffixLabel: "/sqft"
        });
      }
      if (priceStr) {
        layout.right.push({
          type: "secondary" as const,
          value: `₹${priceStr}`
        });
      }
    }

    // DEFAULT
    else {
      // LEFT SIDE (META INFO)
      if (rateStr) {
        layout.left.push({
          type: "primary" as const,
          value: rateStr,
          suffixLabel: "/sqft"
        });
      }
      if (priceStr) {
        layout.left.push({
          type: "secondary" as const,
          value: `₹${priceStr}`
        });
      }

      // RIGHT SIDE (META INFO)
      if (distanceStr) {
        layout.right.push({
          type: "meta" as const,
          value: distanceStr,
          tooltip: "Distance from your property"
        });
      }
      if (builtUpAreasStr) {
        layout.right.push({
          type: "secondary" as const,
          value: builtUpAreasStr,
          suffixLabel: "/sqft"
        });
      }
    }
  }

  // DEFAULT
  else {
    // LEFT SIDE (META INFO)
    if (priceStr) {
      layout.left.push({
        type: "primary" as const,
        value: `₹${priceStr} ${priceSuffix}`
      });
    }
    if (rateStr) {
      layout.left.push({
        type: "secondary" as const,
        value: rateStr,
        suffixLabel: "/sqft"
      });
    }

    // RIGHT SIDE (META INFO)
    if (parkingsStr) {
      layout.right.push({
        type: "meta" as const,
        value: parkingsStr,
        suffixLabel: `parking${Math.max(...parkings) > 1 ? "s" : ""}`
      });
    }
    if (builtUpAreasStr) {
      layout.right.push({
        type: "meta" as const,
        value: builtUpAreasStr,
        suffixLabel: "Sqft"
      });
    }
  }
  return layout;
};

// ---------------------------------------
// 3. GENERIC BLOCK RENDERER
// ---------------------------------------

interface RenderBlockProps {
  block: LayoutBlock;
}
const RenderBlock = t0 => {
  const $ = _c(21);
  const {
    block
  } = t0;
  if (!block?.value) {
    return null;
  }
  let t1;
  if ($[0] !== block.type) {
    t1 = () => {
      switch (block.type) {
        case "primary":
          {
            return "text-lg font-semibold";
          }
        case "secondary":
          {
            return "text-sm opacity-90";
          }
        case "meta":
          {
            return "text-sm";
          }
        default:
          {
            return "";
          }
      }
    };
    $[0] = block.type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getTypeStyles = t1;
  const t2 = block.underline && "underline decoration-dotted underline-offset-2";
  const t3 = (block.onClick || block.button) && "cursor-pointer hover:opacity-100";
  const t4 = block.tooltip && !block.onClick && !block.button && "cursor-help";
  let t5;
  if ($[2] !== t2 || $[3] !== t3 || $[4] !== t4) {
    t5 = [t2, t3, t4].filter(Boolean);
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const additionalClasses = t5.join(" ");
  const t6 = `${getTypeStyles()} ${additionalClasses}`;
  let t7;
  if ($[6] !== t6) {
    t7 = t6.trim();
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const className = t7;
  const t8 = block.suffixLabel ? ` ${block.suffixLabel}` : null;
  let t9;
  if ($[8] !== block.onClick || $[9] !== block.prefixLabel || $[10] !== block.value || $[11] !== className || $[12] !== t8) {
    t9 = <div className={className} onClick={block.onClick}>{block.prefixLabel} {block.value}{t8}</div>;
    $[8] = block.onClick;
    $[9] = block.prefixLabel;
    $[10] = block.value;
    $[11] = className;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const content = t9;
  if (block.tooltip) {
    let t10;
    if ($[14] !== content) {
      t10 = <TooltipTrigger asChild={true}><span>{content}</span></TooltipTrigger>;
      $[14] = content;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    let t11;
    if ($[16] !== block.tooltip) {
      t11 = <TooltipContent><p className="text-sm">{block.tooltip}</p></TooltipContent>;
      $[16] = block.tooltip;
      $[17] = t11;
    } else {
      t11 = $[17];
    }
    let t12;
    if ($[18] !== t10 || $[19] !== t11) {
      t12 = <Tooltip>{t10}{t11}</Tooltip>;
      $[18] = t10;
      $[19] = t11;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    return t12;
  }
  return content;
};

// ---------------------------------------
// 4. MAIN COMPONENT
// ---------------------------------------

interface PropertyInfoOverlayProps {
  forWhat: string;
  supType?: string;
  prices: number[];
  currentRate?: number;
  builtUpAreas: number[];
  parkings: number[];
  comparablePrices?: ComparablePrice[];
  propertyTypes: string[];
  plotAreas: number[];
  supId: string;
  distance?: number;
}
export const PropertyInfoOverlay = React.memo(function PropertyInfoOverlay(t0) {
  const $ = _c(40);
  const {
    forWhat,
    supType,
    prices,
    currentRate,
    builtUpAreas,
    parkings,
    comparablePrices,
    propertyTypes,
    plotAreas,
    supId,
    distance
  } = t0;
  const [showHPIModal, setShowHPIModal] = useState(false);
  let t1;
  if ($[0] !== supId) {
    t1 = state => state.pricingData[supId];
    $[0] = supId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const pricingData = useHousePricingStore(t1);
  let t2;
  if ($[2] !== builtUpAreas || $[3] !== comparablePrices || $[4] !== currentRate || $[5] !== distance || $[6] !== parkings || $[7] !== plotAreas || $[8] !== prices) {
    t2 = {
      prices,
      currentRate,
      builtUpAreas,
      parkings,
      comparablePrices,
      plotAreas,
      distance
    };
    $[2] = builtUpAreas;
    $[3] = comparablePrices;
    $[4] = currentRate;
    $[5] = distance;
    $[6] = parkings;
    $[7] = plotAreas;
    $[8] = prices;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const formatted = useFormattedPropertyData(t2);
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setShowHPIModal(true);
    };
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleEstimatedPriceClick = t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[11] !== forWhat || $[12] !== formatted || $[13] !== parkings || $[14] !== pricingData?.constructionRate || $[15] !== pricingData?.plotRate || $[16] !== propertyTypes || $[17] !== supType) {
    const layout = buildLayout({
      propertyTypes,
      supType,
      forWhat,
      parkings,
      ...formatted,
      constructionRate: pricingData?.constructionRate,
      plotRate: pricingData?.plotRate,
      onEstimatedPriceClick: handleEstimatedPriceClick
    });
    t8 = "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent text-white p-4 pt-8";
    t6 = "flex items-end justify-between";
    t7 = <div className="flex flex-col justify-end space-y-1">{layout.left.map(_temp)}</div>;
    t4 = "flex flex-col justify-end space-y-1 text-right";
    t5 = layout.right.map(_temp2);
    $[11] = forWhat;
    $[12] = formatted;
    $[13] = parkings;
    $[14] = pricingData?.constructionRate;
    $[15] = pricingData?.plotRate;
    $[16] = propertyTypes;
    $[17] = supType;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t4 || $[24] !== t5) {
    t9 = <div className={t4}>{t5}</div>;
    $[23] = t4;
    $[24] = t5;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t6 || $[27] !== t7 || $[28] !== t9) {
    t10 = <div className={t6}>{t7}{t9}</div>;
    $[26] = t6;
    $[27] = t7;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t8) {
    t11 = <div className={t8}>{t10}</div>;
    $[30] = t10;
    $[31] = t8;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== comparablePrices || $[34] !== forWhat || $[35] !== showHPIModal) {
    t12 = forWhat === "sold_and_cancelled" && <HPIInfoModal isOpen={showHPIModal} comparablePrices={comparablePrices} onClose={() => setShowHPIModal(false)} />;
    $[33] = comparablePrices;
    $[34] = forWhat;
    $[35] = showHPIModal;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t11 || $[38] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  return t13;
});
function _temp(block, i) {
  return <RenderBlock key={i} block={block} />;
}
function _temp2(block_0, i_0) {
  return <RenderBlock key={i_0} block={block_0} />;
}